/**
 * On-Call Rotations API Services
 */

import datacentral from '@/utils/axios/datacentral'
import { ResponseArray, ResponseObject } from '@/types/shared'

import {
    Rotation,
    RotationMember,
    CycleTemplate,
    CyclePreviewEntry,
    SetCycleTemplatePayload,
} from '../types/rotation'

import {
    CreateRotationPayload,
    UpdateRotationPayload,
    AddRotationMemberPayload,
    ReorderRotationMembersPayload,
} from '../types/payload'

export const getScheduleRotations = async (
    scheduleId: number
): Promise<ResponseArray<Rotation>> => {
    const response = await datacentral.get<ResponseArray<Rotation>>(
        `/oncall/schedules/${scheduleId}/rotations`
    )
    return response.data
}

export const getRotationById = async (
    scheduleId: number,
    rotationId: number
): Promise<ResponseObject<Rotation>> => {
    const response = await datacentral.get<ResponseObject<Rotation>>(
        `/oncall/schedules/${scheduleId}/rotations/${rotationId}`
    )
    return response.data
}

export const getRotationMembers = async (
    rotationId: number
): Promise<ResponseArray<RotationMember>> => {
    const response = await datacentral.get<ResponseArray<RotationMember>>(
        `/oncall/rotations/${rotationId}/members`
    )
    return response.data
}

export const createRotation = async (payload: {
    scheduleId: number
    data: CreateRotationPayload
}): Promise<ResponseObject<Rotation>> => {
    const response = await datacentral.post<ResponseObject<Rotation>>(
        `/oncall/schedules/${payload.scheduleId}/rotations`,
        payload.data
    )
    return response.data
}

export const updateRotation = async (payload: {
    scheduleId: number
    rotationId: number
    data: UpdateRotationPayload
}): Promise<ResponseObject<Rotation>> => {
    const response = await datacentral.put<ResponseObject<Rotation>>(
        `/oncall/schedules/${payload.scheduleId}/rotations/${payload.rotationId}`,
        payload.data
    )
    return response.data
}

export const deleteRotation = async (payload: {
    scheduleId: number
    rotationId: number
}): Promise<ResponseObject<null>> => {
    const response = await datacentral.delete<ResponseObject<null>>(
        `/oncall/schedules/${payload.scheduleId}/rotations/${payload.rotationId}`
    )
    return response.data
}

export const reorderRotations = async (payload: {
    scheduleId: number
    rotations: { rotation_id: number; rotation_order: number }[]
}): Promise<ResponseObject<Rotation[]>> => {
    const response = await datacentral.put<ResponseObject<Rotation[]>>(
        `/oncall/schedules/${payload.scheduleId}/rotations/reorder`,
        { rotations: payload.rotations }
    )
    return response.data
}

export const addRotationMember = async (payload: {
    rotationId: number
    data: AddRotationMemberPayload
}): Promise<ResponseObject<RotationMember>> => {
    const response = await datacentral.post<ResponseObject<RotationMember>>(
        `/oncall/rotations/${payload.rotationId}/members`,
        payload.data
    )
    return response.data
}

export const removeRotationMember = async (payload: {
    rotationId: number
    memberId: number
}): Promise<ResponseObject<null>> => {
    const response = await datacentral.delete<ResponseObject<null>>(
        `/oncall/rotations/${payload.rotationId}/members/${payload.memberId}`
    )
    return response.data
}

export const reorderRotationMembers = async (payload: {
    rotationId: number
    data: ReorderRotationMembersPayload
}): Promise<ResponseObject<RotationMember[]>> => {
    const response = await datacentral.put<ResponseObject<RotationMember[]>>(
        `/oncall/rotations/${payload.rotationId}/members/reorder`,
        payload.data
    )
    return response.data
}

export const updateRotationMember = async (payload: {
    rotationId: number
    memberId: number
    data: { rotation_order?: number; is_active?: boolean }
}): Promise<ResponseObject<RotationMember>> => {
    const response = await datacentral.put<ResponseObject<RotationMember>>(
        `/oncall/rotations/${payload.rotationId}/members/${payload.memberId}`,
        payload.data
    )
    return response.data
}

export const getRotationTiming = async (
    rotationId: number
): Promise<ResponseObject<{ timing_type: string; time_slots: any[] }>> => {
    const response = await datacentral.get(
        `/oncall/rotations/${rotationId}/timing`
    )
    return response.data
}

export const updateRotationTiming = async (payload: {
    rotationId: number
    data: { timing_type: string; time_slots: any[] }
}): Promise<ResponseObject<any>> => {
    const response = await datacentral.put(
        `/oncall/rotations/${payload.rotationId}/timing`,
        payload.data
    )
    return response.data
}

// ============ CYCLE TEMPLATE ============

export const getCycleTemplate = async (
    rotationId: number
): Promise<ResponseObject<CycleTemplate>> => {
    const response = await datacentral.get<ResponseObject<CycleTemplate>>(
        `/oncall/rotations/${rotationId}/cycle-template`
    )
    return response.data
}

export const setCycleTemplate = async (payload: {
    rotationId: number
    data: SetCycleTemplatePayload
}): Promise<ResponseObject<CycleTemplate>> => {
    const response = await datacentral.put<ResponseObject<CycleTemplate>>(
        `/oncall/rotations/${payload.rotationId}/cycle-template`,
        payload.data
    )
    return response.data
}

export const deleteCycleTemplate = async (
    rotationId: number
): Promise<ResponseObject<{ message: string }>> => {
    const response = await datacentral.delete<
        ResponseObject<{ message: string }>
    >(`/oncall/rotations/${rotationId}/cycle-template`)
    return response.data
}

export const previewCycleTemplate = async (
    rotationId: number,
    startDate: string,
    endDate: string
): Promise<ResponseArray<CyclePreviewEntry>> => {
    const response = await datacentral.get<ResponseArray<CyclePreviewEntry>>(
        `/oncall/rotations/${rotationId}/cycle-template/preview`,
        { params: { start_date: startDate, end_date: endDate } }
    )
    return response.data
}
